import { SocketMessage, SocketMessageEvent } from '../sockets/messages';
import { IProcessVideoOutput } from '../video/video';
import { TranslateOptions, translate } from '../i18n/translate';
import { UploadId } from '../upload';
import { BuilderTemplateId, AssetIdAndLayerId } from '../builderTemplate';
import { DeviceId } from '../devices';
import { PublishedArtifactId, PublishedArtifactGroupId } from '../publishedArtifact';
import { JobFailure } from './job-failure';

export interface IBaseVideoData {
    video: UploadId;
    removeAudio: boolean;
}

export interface IVideoData extends IBaseVideoData {
    image: UploadId;
    dimensions: {
        width: number;
        height: number;
    };
}

export interface IVideoRequestData {
    image: UploadId;
    videoUploadId?: UploadId | null;
    videoUrl?: string | null;
    dimensions: {
        width: number;
        height: number;
    };
    removeAudio: boolean;
}

export interface PartialVideoAuditDataGraphQLBackend {
    builderTemplateId: BuilderTemplateId;
    deviceId?: DeviceId | null;
    linkedAssetLibraryAssetIds?: AssetIdAndLayerId[];
    plannerId?: number;
    publishedArtifactGroupId?: PublishedArtifactGroupId | null;
    publishedArtifactId?: PublishedArtifactId;
    scheduledPublishGroupId?: unknown;
}

/**
 * This was originally bad typing being shared between the front and backend.
 *
 * I've split it into two separate types so it looks terrible, but its correct.
 */
export interface IPublishVideoRequestBackend<CD> {
    channelData: CD;
    videoData: IVideoRequestData;
    auditData: PartialVideoAuditDataGraphQLBackend;
}

/**
 * This is bad, it should be only on the frontend.
 * Refactor too hard at this point.
 */
interface PartialVideoAuditDataGraphQLFrontend {
    builderTemplateId: BuilderTemplateId;
    deviceId?: DeviceId | null;
    linkedAssetLibraryAssetIds?: AssetIdAndLayerId[];
    plannerId?: number;
    publishedArtifactGroupId?: PublishedArtifactGroupId | null;
    publishedArtifactId?: PublishedArtifactId;
    scheduledPublishGroupId?: string | null;
}

export interface IPublishVideoRequest<CD> {
    channelData: CD;
    videoData: IVideoRequestData;
    auditData: PartialVideoAuditDataGraphQLFrontend;
}

export type IPublishVideoToLocalRequest = IPublishVideoRequest<Record<string, any>>;

export interface IPublishVideoResponse extends IProcessVideoOutput {
    executionId?: string;
}

export function parseJobFailure(jobFailure: JobFailure): string | undefined {

    if (jobFailure && jobFailure.Cause) {
        try {
            const causeBody: TranslateOptions = JSON.parse(jobFailure.Cause);
            return translate(causeBody.key, causeBody.options);
        } catch (err) {
            if (!err || err.name !== 'SyntaxError') {
                throw err;
            } else {
                return jobFailure.Cause;
            }
        }

    } else if (jobFailure && jobFailure.Error) {
        return jobFailure.Error;
    } else {
        return undefined; // Blech
    }
}

export function isJobFailure(result: any): result is JobFailure {
    return !!(result && result.Cause);
}

export interface IProcessVideoProgress {
    executionId: string;
    // This should always be a value between 0 and 100
    timeMark: number;
    // This should always be set to 100
    duration: number;
    timestamp: number; // TODO: add this to all messages
}

export interface VideoProcessingFinishedMessage extends SocketMessage<IPublishVideoResponse> {
    name: 'videoProcessJobFinished';
}
export const VideoProcessingFinishedEvent = new SocketMessageEvent<IPublishVideoResponse>('videoProcessJobFinished');

export interface VideoProcessingFailedMessage extends SocketMessage<JobFailure> {
    name: 'videoProcessJobFailed';
}
export const VideoProcessingFailedEvent = new SocketMessageEvent<JobFailure>('videoProcessJobFailed');

export interface VideoProcessingProgressMessage extends SocketMessage<IProcessVideoProgress> {
    name: 'videoProcessJobProgress';
}
export const VideoProcessingProgressEvent = new SocketMessageEvent<IProcessVideoProgress>('videoProcessJobProgress');
